// SuggestionChips (DESIGN §3.4.3): Einstiegsfragen je Rolle. `cards` (AI1 Desktop): Raster 2 × 2,
// Karte 80 hoch, Radius 16, Icon-Kreis 32 `accent-soft`, Frage `label` zwei Zeilen, Bereich `small`
// `fg-tertiary`. `pills` (AS1, AI1 mobil, Home-Widget): Sekundär-Pills 36 in einer wischbaren Zeile
// mit Ausblendung am Rand. Klick übergibt die Frage (der Aufrufer setzt den Text und sendet).
import { useRef, type ReactNode } from 'react';
import { useScrollFade } from './_lib/useScrollFade';
import './_lib/motion.css';
import { cn } from '../basis/kern';

export interface Suggestion {
  id: string;
  /** Voller Fragetext (auch Name des Knopfs). */
  text: string;
  /** Modul-Icon 16 (Phosphor), nur `cards`. */
  icon?: ReactNode;
  /** Bereich unter der Frage, z. B. „Goals“, nur `cards`. */
  area?: string;
}

export interface SuggestionChipsProps {
  suggestions: Suggestion[];
  onSelect: (suggestion: Suggestion) => void;
  variant?: 'cards' | 'pills';
  /** Fläche unter den Pills (Pill-Farbe). */
  surface?: 'page' | 'card';
  'aria-label'?: string;
  className?: string;
}

function SuggestionPills({
  suggestions,
  onSelect,
  surface,
  ariaLabel,
  className,
}: Pick<SuggestionChipsProps, 'suggestions' | 'onSelect' | 'surface' | 'className'> & {
  ariaLabel: string;
}) {
  const rowRef = useRef<HTMLUListElement>(null);
  useScrollFade(rowRef);
  return (
    <ul
      ref={rowRef}
      aria-label={ariaLabel}
      className={cn('bar-st-scroll-x bar-st-fade-x flex min-w-0 gap-2 overflow-x-auto', className)}
    >
      {suggestions.map((suggestion) => (
        <li key={suggestion.id} className="shrink-0">
          <button
            type="button"
            onClick={() => onSelect(suggestion)}
            className={cn(
              'inline-flex h-9 items-center rounded-full px-4 text-button whitespace-nowrap text-fg',
              'transition-[background-color,transform] duration-(--bar-dur-fast) ease-standard active:scale-[.97]',
              surface === 'card'
                ? 'bg-pill-card hover:bg-pill-card-hover'
                : 'bg-pill-page hover:bg-pill-page-hover',
            )}
          >
            {suggestion.text}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function SuggestionChips({
  suggestions,
  onSelect,
  variant = 'cards',
  surface = 'page',
  'aria-label': ariaLabel = 'Suggested questions',
  className,
}: SuggestionChipsProps) {
  if (variant === 'pills') {
    return (
      <SuggestionPills
        suggestions={suggestions}
        onSelect={onSelect}
        surface={surface}
        ariaLabel={ariaLabel}
        className={className}
      />
    );
  }
  return (
    <ul aria-label={ariaLabel} className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2', className)}>
      {suggestions.map((suggestion) => (
        <li key={suggestion.id} className="min-w-0">
          <button
            type="button"
            onClick={() => onSelect(suggestion)}
            className={cn(
              'flex h-20 w-full items-center gap-3 rounded-xl bg-card px-4 text-left',
              'transition-[background-color,transform] duration-(--bar-dur-fast) ease-standard hover:bg-card-hover active:scale-[.99]',
            )}
          >
            {suggestion.icon ? (
              <span
                aria-hidden="true"
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-icon-circle text-fg [&>svg]:size-4"
              >
                {suggestion.icon}
              </span>
            ) : null}
            <span className="flex min-w-0 flex-col">
              <span className="line-clamp-2 text-label text-fg">{suggestion.text}</span>
              {suggestion.area ? (
                <span className="truncate text-small text-fg-tertiary">{suggestion.area}</span>
              ) : null}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
